import React, { useState, useEffect, useMemo } from "react";

import {
  Building2,
  Users,
  Settings,
  CheckCircle2,
  Globe,
  Mail,
  Phone,
  TrendingUp,
  Award,
  Target,
  Zap,
  AlertTriangle,
  Lightbulb,
  ShieldCheck,
  ExternalLink,
  AlertCircle,
  Star,
  Share2,
  BarChart3,
  CreditCard,
  Wallet,
  Layers,
  User,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../config";
import { motion } from "framer-motion";

const StartupDetailsPage = () => {
  const [activeTab, setActiveTab] = useState("Startup info");
  const [activityData, setActivitydata] = useState([]);
  const [startupData, setStartupData] = useState(null);
  const [businessModel, setBusinessModel] = useState(null);
  const [startupScore, setStartupScore] = useState(null);
  const [teamDetails, setTeamDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const { startupId } = useParams();

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        activityRes,
        startupRes,
        businessHistoryRes,
        validationHistoryRes,
        teamRes,
      ] = await Promise.all([
        apiClient.post(`/api/activity/get-all`, { startupId }),
        apiClient.get(`/api/startup/getStartupInfo/${startupId}`),
        apiClient.get(`/api/business-model/history/${startupId}`),
        apiClient.get(`/api/startup/recent-validations/${startupId}`),
        apiClient.get(`/api/cofounders/${startupId}`),
      ]);

      setActivitydata(activityRes?.data?.data || []);
      setStartupData(startupRes?.data?.startup || null);
      setBusinessModel(businessHistoryRes?.data?.data?.[0] || null);
      setStartupScore(validationHistoryRes?.data?.[0] || null);
      setTeamDetails(teamRes?.data?.data || []);
    } catch (error) {
      console.error(
        "❌ ERROR FETCHING DATA:",
        error?.response || error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startupId) fetchAllData();
  }, [startupId]);

  const stats = useMemo(() => {
    const total = activityData.length || 0;
    const accessible = activityData.filter((a) => a.is_accessible).length;
    const completed = activityData.filter((a) => a.is_completed).length;

    const progressPercent =
      total > 0 ? Math.round((completed / total) * 100) : 0;

    const accessiblePercent =
      total > 0 ? Math.round((accessible / total) * 100) : 0;

    return { total, accessible, completed, progressPercent, accessiblePercent };
  }, [activityData]);

  const readinessData = useMemo(() => {
    const getStatus = (val) => {
      if (val >= 80) return "Excellent";
      if (val >= 40) return "In Progress";
      return "Early Stage";
    };

    const ideaTasks = activityData.slice(0, 5);

    const ideaScore =
      ideaTasks.length > 0
        ? Math.round(
            (ideaTasks.filter((t) => t.is_completed).length /
              ideaTasks.length) *
              100,
          )
        : 0;

    const invPaths = ["pitchdeck", "valuation", "captable", "pitch"];

    const invTasks = activityData.filter((t) =>
      invPaths.some((p) => t.activity_path?.toLowerCase().includes(p)),
    );

    const invScore =
      invTasks.length > 0
        ? Math.round(
            (invTasks.filter((t) => t.is_completed).length / invTasks.length) *
              100,
          )
        : 0;

    return [
      {
        label: "Idea Viability",
        val: ideaScore,
        status: getStatus(ideaScore),
        color: "#10b981",
      },
      {
        label: "Investment Readiness",
        val: invScore,
        status: getStatus(invScore),
        color: "#3b82f6",
      },
      {
        label: "Execution Score",
        val: stats.progressPercent,
        status: getStatus(stats.progressPercent),
        color: "#f59e0b",
      },
    ];
  }, [activityData, stats.progressPercent]);

  const StartupInfoTab = () => (
    <div className="row g-4">
      <div className="col-md-8">
        <div className="card border-0 bg-light p-4 rounded-4 h-100">
          <div className="mb-4">
            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
              <Target size={20} className="text-primary" /> Elevator Pitch
            </h5>
            <p className="text-dark lead" style={{ fontSize: "1.05rem" }}>
              {startupData?.elevatorPitch || "No pitch provided yet."}
            </p>
          </div>
          <hr className="opacity-10" />
          <div className="mb-4">
            <h6 className="fw-bold text-muted text-uppercase small mb-2">
              Problem Statement
            </h6>
            <p className="text-secondary">
              {startupData?.problemStatement || "No problem statement defined."}
            </p>
          </div>
          <div className="row g-3 mt-2">
            {[
              { label: "Industry", value: startupData?.industry },
              { label: "Country", value: startupData?.country },
              {
                label: "Company Reg.",
                value: startupData?.companyRegistered ? "Yes" : "No",
                color: startupData?.companyRegistered
                  ? "text-success"
                  : "text-danger",
              },
            ].map((item, i) => (
              <div className="col-6 col-md-4" key={i}>
                <small className="text-muted d-block">{item.label}</small>
                <span className={`fw-semibold ${item.color || ""}`}>
                  {item.value || "N/A"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="col-md-4">
        <div className="vstack gap-4">
          <div className="card border-0 shadow-sm p-3 rounded-4">
            <h6 className="fw-bold mb-3">Contact Details</h6>
            <div className="vstack gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary-subtle p-2 rounded-3 text-primary">
                  <Globe size={18} />
                </div>
                <div className="text-truncate">
                  <small className="text-muted d-block">Website</small>
                  <a
                    href={startupData?.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-none small fw-medium text-primary"
                  >
                    {startupData?.website?.replace(/^https?:\/\//, "") ||
                      "Not set"}
                  </a>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-success-subtle p-2 rounded-3 text-success">
                  <Mail size={18} />
                </div>
                <div className="text-truncate">
                  <small className="text-muted d-block">Email</small>
                  <span className="small fw-medium">
                    {startupData?.email || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm p-3 rounded-4 bg-white">
            <h6 className="fw-bold mb-3">Operational Status</h6>
            <div className="vstack gap-2">
              <div className="d-flex justify-content-between align-items-center p-2 rounded-3 bg-light">
                <small className="fw-medium">Current Stage</small>
                <span className="badge bg-warning text-dark">
                  {startupData?.startupStage || "Ideation"}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center p-2 rounded-3 bg-light">
                <small className="fw-medium">Revenue</small>
                <span
                  className={`badge ${startupData?.revenue?.generated ? "bg-success" : "bg-secondary"}`}
                >
                  {startupData?.revenue?.generated
                    ? "Generating"
                    : "Pre-Revenue"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const BusinessModelCanvas = ({ businessModel }) => {
    if (!businessModel || !businessModel.generatedCanvas) {
      return (
        <div className="text-center py-5 border rounded-4 bg-light">
          <Layers size={48} className="text-muted mb-3 opacity-20" />
          <p className="text-muted">No Business Model Data Available</p>
        </div>
      );
    }

    const canvas = businessModel.generatedCanvas;
    const iconMap = {
      Problem: <AlertCircle size={18} className="text-danger" />,
      Solution: <Lightbulb size={18} className="text-warning" />,
      "Unique Value Proposition": <Star size={18} className="text-primary" />,
      Relationships: <Users size={18} className="text-info" />,
      "Customer Segments": <Layers size={18} className="text-success" />,
      "Key Metrics": <BarChart3 size={18} className="text-indigo" />,
      Channels: <Share2 size={18} className="text-purple" />,
      "Cost Structure": <Wallet size={18} className="text-secondary" />,
      "Revenue Streams": <CreditCard size={18} className="text-success" />,
    };

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="fw-bold text-dark mb-1">
              {businessModel.businessName}
            </h4>
            <span className="badge bg-primary-subtle text-primary rounded-pill px-3">
              {businessModel.businessType}
            </span>
          </div>
        </div>
        <div className="row g-3">
          {Object.entries(canvas).map(([key, value]) => (
            <div
              className={
                key.includes("Cost") || key.includes("Revenue")
                  ? "col-md-6"
                  : "col-md-4"
              }
              key={key}
            >
              <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden border-top border-primary border-3">
                <div className="card-header border-0 bg-white pt-3 px-3">
                  <div className="d-flex align-items-center gap-2">
                    {iconMap[key] || <Settings size={18} />}
                    <h6 className="fw-bold mb-0 text-dark small text-uppercase">
                      {key}
                    </h6>
                  </div>
                </div>
                <div className="card-body px-3 pb-4">
                  <p
                    className="text-secondary small mb-0"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const StartupScoreTab = () => {
    if (!startupScore)
      return (
        <div className="text-center py-5">No validation records found.</div>
      );
    const metrics = [
      {
        title: "Strengths",
        data: startupScore.strengths,
        color: "success",
        icon: <ShieldCheck size={18} />,
      },
      {
        title: "Weaknesses",
        data: startupScore.weaknesses,
        color: "danger",
        icon: <AlertTriangle size={18} />,
      },
      {
        title: "Opportunities",
        data: startupScore.opportunities,
        color: "info",
        icon: <Lightbulb size={18} />,
      },
      {
        title: "Risks",
        data: startupScore.risks,
        color: "warning",
        icon: <Zap size={18} />,
      },
    ];

    return (
      <div className="row g-4">
        <div className="col-md-4">
          <div className="card border-0 bg-primary text-white p-4 rounded-4 text-center h-100 d-flex flex-column justify-content-center shadow">
            <h1 className="display-2 fw-bold mb-0">{startupScore.score}</h1>
            <p className="text-uppercase fw-bold small opacity-75">
              Startup IdeaValidation Score
            </p>
          </div>
        </div>
        <div className="col-md-8">
          <div className="row g-3">
            {metrics.map((box) => (
              <div className="col-md-6" key={box.title}>
                <div
                  className={`card border-0 bg-${box.color}-subtle p-3 rounded-4 h-100`}
                >
                  <h6
                    className={`fw-bold text-${box.color} d-flex align-items-center gap-2 mb-2`}
                  >
                    {box.icon} {box.title}
                  </h6>
                  <ul className="list-unstyled mb-0 small vstack gap-1">
                    {box.data?.map((item, i) => (
                      <li key={i} className="d-flex gap-2">
                        <span>•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const ActivityProgress = () => {
    // Calculate completion percentage
    const completedCount = activityData.filter((a) => a.is_completed).length;
    const totalCount = activityData.length;
    const progressPercent = Math.round((completedCount / totalCount) * 100);

    return (
      <div className="border-0 shadow-sm rounded-4 p-4 bg-white row">
        {/* ===== READINESS CARD ===== */}
        <div className="col-lg-4 d-flex flex-column gap-3">
          <div
            className="card border-0 rounded-4 p-4 shadow-sm"
            style={{
              // backgroundColor: currentTheme.backgroundColor.card,
              border: `1px solid dark`,
              minHeight: "280px",
            }}
          >
            <h6
              className="fw-bold mb-4"
              // style={{ color: currentTheme.textColor.primary }}
            >
              Startup Readiness
            </h6>
            <div className="d-flex flex-column gap-4">
              {readinessData.map((item, i) => (
                <div key={i}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold" style={{ fontSize: "13px" }}>
                      {item.label}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: item.color,
                      }}
                    >
                      {item.val}% • {item.status}
                    </span>
                  </div>
                  <div
                    className="position-relative d-flex align-items-center"
                    style={{ height: "12px" }}
                  >
                    <div
                      className="position-absolute w-100 rounded-pill"
                      style={{
                        height: "6px",
                        backgroundColor: "#f1f5f9",
                      }}
                    />
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.val}%` }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="position-absolute rounded-pill shadow-sm"
                      style={{
                        height: "10px",
                        backgroundColor: item.color,
                        border: "1px solid rgba(255,255,255,0.2)",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ===== ANALYTICS CARD ===== */}
        <div className="col-lg-4 d-flex flex-column gap-3">
          <div
            className="card border-0 rounded-4 p-4 shadow-sm"
            style={{
              // backgroundColor: currentTheme.backgroundColor.card,
              border: `1px solid dark`,
              minHeight: "280px",
            }}
          >
            <h6 className="fw-bold mb-4 text-capitalize ">
              {startupData.selectedPlan} Program Analytics
            </h6>
            <div className="row align-items-center">
              <div className="col-6 position-relative d-flex justify-content-center">
                <svg
                  viewBox="0 0 36 36"
                  style={{ width: "130px", transform: "rotate(-90deg)" }}
                >
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke={"#f1f5f9"}
                    strokeWidth="4"
                  />
                  <motion.circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="4"
                    strokeDasharray={`${stats.accessiblePercent}, 100`}
                    initial={{ strokeDasharray: "0, 100" }}
                    animate={{
                      strokeDasharray: `${stats.accessiblePercent}, 100`,
                    }}
                    transition={{ duration: 1 }}
                  />
                  <motion.circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="#10B981"
                    strokeWidth="4"
                    strokeDasharray={`${stats.progressPercent}, 100`}
                    initial={{ strokeDasharray: "0, 100" }}
                    animate={{
                      strokeDasharray: `${stats.progressPercent}, 100`,
                    }}
                    transition={{ duration: 1, delay: 0.3 }}
                  />
                </svg>
                <div className="position-absolute top-50 start-50 translate-middle text-center">
                  <div className="fw-bold h5 mb-0">
                    {stats.progressPercent}%
                  </div>
                  <div
                    className="small text-muted"
                    style={{ fontSize: "14px" }}
                  >
                    Done
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="d-flex flex-column gap-2">
                  <div className="small border-start border-4 border-success ps-2">
                    <div className="text-muted" style={{ fontSize: "14px" }}>
                      Completed
                    </div>
                    <div className="fw-bold">{stats.completed} Tasks</div>
                  </div>
                  <div className="small border-start border-4 border-primary ps-2">
                    <div className="text-muted" style={{ fontSize: "14px" }}>
                      Accessible
                    </div>
                    <div className="fw-bold">{stats.accessible} Tasks</div>
                  </div>
                  <div className="small border-start border-4 border-secondary ps-2">
                    <div className="text-muted" style={{ fontSize: "14px" }}>
                      Total
                    </div>
                    <div className="fw-bold">{stats.total} Tasks</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      // </div>
    );
  };
  const TeamMembersTab = () => (
    <div className="py-4">
      <style>{`
        .founder-card {
            transition: all 0.3s ease;
            
            border-radius: 20px;
            position: relative;
            overflow: hidden;
        }
        .founder-card:hover {
            transform: translateY(-5px);
            
        }
        .equity-badge {
            position: absolute;
            top: 15px;
            right: 15px;
            background: rgba(30, 200, 255, 0.1);
            font-weight: 700;
            padding: 5px 12px;
            border-radius: 10px;
            font-size: 0.75rem;
            border: 1px solid rgba(30, 200, 255, 0.2);
        }
        .social-btn {
            width: 35px;
            height: 35px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: "#f8f9fa";
            transition: 0.3s;
            text-decoration: none;
        }
        .social-btn:hover {
            color: white !important;
        }
      `}</style>

      <div className="row g-4">
        {loading ? (
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-info" role="status"></div>
          </div>
        ) : teamDetails.length > 0 ? (
          teamDetails.map((founder, index) => (
            <div className="col-xl-4 col-md-6" key={index}>
              <div className="card founder-card h-100">
                <span className="equity-badge">{founder.equity}% Equity</span>

                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="flex-shrink-0">
                      <img
                        src="/assets/assets/images/profile/user-1.jpg"
                        alt={founder.name}
                        className="rounded-circle border border-2 border-info"
                        style={{
                          width: "70px",
                          height: "70px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div>
                      <h5
                        className="mb-0 fw-bold"
                        // style={{ color: themeStyles.text }}
                      >
                        {founder.name}
                      </h5>
                      <small className="text-info fw-semibold">
                        {founder.expertise}
                      </small>
                    </div>
                  </div>

                  <p
                    className="small mb-3"
                    style={{
                      // color: themeStyles.muted,
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {founder.bio || "No bio available for this founder."}
                  </p>

                  {/* <hr style={{ borderColor: themeStyles.border }} /> */}

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-2">
                      <a
                        href={`mailto:${founder.email}`}
                        className="social-btn"
                        title="Email"
                      >
                        <i className="ti ti-mail"></i>
                      </a>
                      <a
                        href={founder.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        className="social-btn"
                        title="LinkedIn"
                      >
                        <i className="ti ti-brand-linkedin"></i>
                      </a>
                      <a
                        href={`tel:${founder.phone}`}
                        className="social-btn"
                        title="Phone"
                      >
                        <i className="ti ti-phone"></i>
                      </a>
                    </div>
                    {/* <button
                      className="btn btn-sm rounded-pill px-3"
                      style={{
                        border: `1px solid #000`,
                        color: "#000",
                      }}
                    >
                      View Profile
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-12 text-center py-4">
            <p className="text-muted">No founder details found.</p>
          </div>
        )}
      </div>
    </div>
  );

  const tabs = [
    { name: "Startup info", icon: Building2 },
    { name: "Team Members", icon: Users },
    { name: "Startup Score", icon: Award },
    { name: "Bussiness Model Score", icon: Target },
    { name: "Activity progress", icon: TrendingUp },
  ];

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        Loading Startup Data...
      </div>
    );

  return (
    <div className=" py-4  min-vh-100 px-3">
      <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-4 shadow-sm border border-white">
        <div>
          <h2 className="fw-bold mb-1 text-dark h4">
            {startupData?.startupName} Startup Profile
          </h2>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-primary-subtle text-primary">
              Industry: {startupData?.industry}
            </span>
            <span className="text-muted small">
              • Created {new Date(startupData?.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="card-header bg-white border-bottom p-0 overflow-auto scrollbar-hide">
          <ul className="nav nav-tabs border-0 flex-nowrap">
            {tabs.map(({ name, icon: Icon }) => (
              <li className="nav-item" key={name}>
                <button
                  onClick={() => setActiveTab(name)}
                  className={`nav-link border-0 border-bottom border-3 px-4 py-3 d-flex align-items-center gap-2 fw-semibold text-nowrap 
                    ${activeTab === name ? "active border-primary text-primary" : "text-secondary border-transparent"}`}
                >
                  <Icon size={16} /> {name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-body p-4 bg-white">
          {activeTab === "Startup info" && <StartupInfoTab />}
          {activeTab === "Bussiness Model Score" && (
            <BusinessModelCanvas businessModel={businessModel} />
          )}
          {activeTab === "Startup Score" && <StartupScoreTab />}
          {activeTab === "Activity progress" && <ActivityProgress />}
          {activeTab === "Team Members" && <TeamMembersTab />}
        </div>
      </div>
    </div>
  );
};

export default StartupDetailsPage;
